import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, OverlayTrigger, Spinner, Table, Tooltip } from "react-bootstrap";
import { AppCheckbox, AppDateField, AppSelect } from "../components/form";
import { authFetch } from "../api/authFetch";

const API = "/api/production";
const API_WS = "/api/workshop-payroll";

function formatYMDLocal(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

function parseLocalYMD(s) {
    if (!s || typeof s !== "string") return new Date(NaN);
    const parts = s.split("-").map(Number);
    const [y, mo, da] = parts;
    if (!y || !mo || !da) return new Date(NaN);
    return new Date(y, mo - 1, da);
}

function mondayOfWeekContaining(ymd) {
    const d = parseLocalYMD(ymd);
    if (Number.isNaN(d.getTime())) return ymd;
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const mon = new Date(d.getFullYear(), d.getMonth(), diff);
    return formatYMDLocal(mon);
}

function sundayOfSameWeek(mondayYmd) {
    const d = parseLocalYMD(mondayYmd);
    if (Number.isNaN(d.getTime())) return mondayYmd;
    const sun = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 6);
    return formatYMDLocal(sun);
}

function weekMondayTodayYMD() {
    const d = new Date();
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const mon = new Date(d.getFullYear(), d.getMonth(), diff);
    return formatYMDLocal(mon);
}

function formatUkDMYFromYmd(ymd) {
    const d = parseLocalYMD(ymd);
    if (Number.isNaN(d.getTime())) return "";
    const day = String(d.getDate()).padStart(2, "0");
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    const y = d.getFullYear();
    return `${day}.${mo}.${y}`;
}

function formatQty(n) {
    const x = Number(n);
    if (!Number.isFinite(x)) return "—";
    return x.toLocaleString("uk-UA", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function getVal(row, colKey) {
    if (colKey === "plan")     return Number(row.planQty)     || 0;
    if (colKey === "delivery") return Number(row.deliveryQty) || 0;
    if (colKey === "payroll")  return Number(row.payrollQty)  || 0;
    return 0;
}

/** Клітинка з Bootstrap-тултипом при наведенні (якщо є breakdown) */
function BreakdownCell({ value, breakdown, id }) {
    if (!breakdown) return <>{value}</>;
    return (
        <OverlayTrigger
            placement="top"
            overlay={
                <Tooltip id={id} style={{ textAlign: "left" }}>
                    {breakdown.split(", ").map((item, i) => (
                        <div key={i}>{item}</div>
                    ))}
                </Tooltip>
            }
        >
            <span style={{ cursor: "help", textDecoration: "underline dotted", textUnderlineOffset: 3 }}>
                {value}
            </span>
        </OverlayTrigger>
    );
}

/** Ієрархічний список виконання плану по SKU */
function PlanDetailList({ products, onlyDiscrepancies }) {
    if (!products?.length) {
        return <p className="text-muted text-center py-4">Немає позицій у плані.</p>;
    }

    const filtered = onlyDiscrepancies
        ? products.map(p => ({
              ...p,
              types: p.types.map(t => ({
                  ...t,
                  colors: t.colors.map(c => ({
                      ...c,
                      items: c.items.filter(i => Math.abs(i.deliveryQty - i.planQty) > 1e-6),
                  })).filter(c => c.items.length > 0),
              })).filter(t => t.colors.length > 0),
          })).filter(p => p.types.length > 0)
        : products;

    if (filtered.length === 0) {
        return <p className="text-muted text-center py-4">Немає рядків із розбіжностями.</p>;
    }

    const COL = { minWidth: 52, textAlign: "right" };

    return (
        <div className="plan-detail-list" style={{ fontSize: "0.82rem" }}>
            {/* Column header row */}
            <div className="d-flex align-items-center gap-2 px-2 border-bottom text-muted fw-semibold mb-1" style={{ fontSize: "0.72rem", paddingTop: 3, paddingBottom: 3 }}>
                <span className="flex-fill">Найменування / SKU</span>
                <span style={COL}>План</span>
                <span style={COL}>Поставки</span>
                <span style={COL}>Різниця</span>
            </div>

            {filtered.map(product => {
                const showTypeHeaders = product.types.length > 1
                    || (product.types.length === 1 && product.types[0].type !== "—");
                const diff = product.deliveryTotal - product.planTotal;
                const diffCls = diff > 0 ? "text-success" : diff < 0 ? "text-danger" : "text-muted";

                return (
                    <div key={product.productKey} className="border-bottom" style={{ paddingBottom: 4, marginBottom: 4 }}>
                        {/* Product header */}
                        <div className="d-flex align-items-baseline gap-2 fw-semibold" style={{ paddingTop: 3, paddingBottom: 2 }}>
                            <span className="flex-fill">
                                {product.name}{" "}
                                <span className="text-muted fw-normal">({formatQty(product.planTotal)})</span>
                            </span>
                            <span className="font-monospace" style={COL}>{formatQty(product.planTotal)}</span>
                            <span className="font-monospace" style={COL}>{formatQty(product.deliveryTotal)}</span>
                            <span className={`font-monospace ${diffCls}`} style={COL}>
                                {diff !== 0 ? (diff > 0 ? "+" : "") + formatQty(diff) : "—"}
                            </span>
                        </div>

                        {product.types.map(({ type, colors }) => (
                            <div key={type} style={{ paddingLeft: 12 }}>
                                {showTypeHeaders && (
                                    <div className="text-muted" style={{ fontSize: "0.75rem", paddingBottom: 1 }}>
                                        Тип: <span className="text-body">{type}</span>
                                    </div>
                                )}
                                {colors.map(({ color, items }) => (
                                    <div key={color} style={{ paddingLeft: 12 }}>
                                        <div className="text-secondary fw-medium" style={{ fontSize: "0.75rem", paddingTop: 1, paddingBottom: 1 }}>{color}</div>
                                        <ul className="list-unstyled mb-0" style={{ paddingLeft: 12 }}>
                                            {items.map((item, idx) => {
                                                const d = item.deliveryQty - item.planQty;
                                                const cls = d >= 0 ? (d > 0 ? "text-success" : "") : "text-danger";
                                                return (
                                                    <li
                                                        key={`${item.sku}-${idx}`}
                                                        className="d-flex align-items-center gap-2 border-bottom border-light"
                                                        style={{ paddingTop: 2, paddingBottom: 2 }}
                                                    >
                                                        <span className="text-nowrap flex-fill">
                                                            {item.size && item.size !== "—" && (
                                                                <>Розмір <strong>{item.size}</strong>{" "}</>
                                                            )}
                                                            {item.sku && (
                                                                <span className="text-muted" style={{ fontSize: "0.75rem" }}>SKU {item.sku}</span>
                                                            )}
                                                        </span>
                                                        <span className="font-monospace text-muted" style={COL}>{formatQty(item.planQty)}</span>
                                                        <span className="font-monospace" style={COL}>{formatQty(item.deliveryQty)}</span>
                                                        <span className={`font-monospace ${cls}`} style={COL}>
                                                            {d !== 0 ? (d > 0 ? "+" : "") + formatQty(d) : "—"}
                                                        </span>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                );
            })}
        </div>
    );
}

export default function ProductionReconciliationPage() {
    const [workshops, setWorkshops]           = useState([]);
    const [workshopId, setWorkshopId]         = useState("all");
    const [weekMonday, setWeekMonday]         = useState(() => weekMondayTodayYMD());
    const [loading, setLoading]               = useState(false);
    const [error, setError]                   = useState("");
    const [data, setData]                     = useState(null);
    const [onlyDiscrepancies, setOnlyDiscrepancies] = useState(false);

    // Mode: "delivery_check" | "plan_check"
    const [mode, setMode] = useState("delivery_check");

    // Plan
    const [plans, setPlans]         = useState([]);
    const [planId, setPlanId]       = useState("");
    const [detail, setDetail]       = useState(null); // per-SKU data for plan_check mode
    const [detailLoading, setDetailLoading] = useState(false);

    // Derived from mode
    const showPlan    = mode === "plan_check";
    const showPayroll = mode === "delivery_check";
    const diffA = "delivery";
    const diffB = showPlan ? "plan" : "payroll";

    const weekSunday     = useMemo(() => sundayOfSameWeek(weekMonday), [weekMonday]);
    const weekRangeLabel = useMemo(() => {
        const a = formatUkDMYFromYmd(weekMonday);
        const b = formatUkDMYFromYmd(weekSunday);
        return a && b ? `${a} — ${b}` : "";
    }, [weekMonday, weekSunday]);

    const onWeekPick   = (ymd) => setWeekMonday(mondayOfWeekContaining(ymd));
    const onModeChange = (newMode) => setMode(newMode);

    // Load workshops
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await authFetch(`${API_WS}/workshops`, { credentials: "include" });
                const list = await r.json().catch(() => []);
                if (!r.ok) throw new Error(list.error || r.statusText);
                if (!cancelled) setWorkshops(list);
            } catch (e) {
                if (!cancelled) setError(e.message || "Помилка завантаження цехів");
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Load plans list
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await authFetch(`${API}/reconciliation/plans`, { credentials: "include" });
                const list = await r.json().catch(() => []);
                if (!cancelled) setPlans(Array.isArray(list) ? list : []);
            } catch { /* silent */ }
        })();
        return () => { cancelled = true; };
    }, []);

    // Load summary reconciliation (flat table)
    const loadReconciliation = useCallback(async () => {
        if (!workshopId) return;
        setLoading(true);
        setError("");
        try {
            const from = new Date(weekMonday).toISOString();
            const to   = new Date(`${weekSunday}T23:59:59`).toISOString();
            const qs   = new URLSearchParams({ workshopId, from, to });
            if (planId) qs.set("planId", planId);
            const r = await authFetch(`${API}/reconciliation?${qs}`, { credentials: "include" });
            const j = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(j.error || r.statusText);
            setData(j);
        } catch (e) {
            setError(e.message || "Помилка");
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [workshopId, weekMonday, weekSunday, planId]);

    useEffect(() => { void loadReconciliation(); }, [loadReconciliation]);

    // Load per-SKU detail (only in plan_check mode with planId selected)
    const loadDetail = useCallback(async () => {
        if (mode !== "plan_check" || !planId) { setDetail(null); return; }
        setDetailLoading(true);
        try {
            const from = new Date(weekMonday).toISOString();
            const to   = new Date(`${weekSunday}T23:59:59`).toISOString();
            const qs   = new URLSearchParams({ planId, workshopId, from, to });
            const r = await authFetch(`${API}/reconciliation/plan-detail?${qs}`, { credentials: "include" });
            const j = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(j.error || r.statusText);
            setDetail(j);
        } catch { setDetail(null); }
        finally { setDetailLoading(false); }
    }, [mode, planId, workshopId, weekMonday, weekSunday]);

    useEffect(() => { void loadDetail(); }, [loadDetail]);

    const rows = Array.isArray(data?.rows) ? data.rows : [];

    const displayedRows = useMemo(() => {
        if (!onlyDiscrepancies) return rows;
        return rows.filter((row) => {
            const d = getVal(row, diffA) - getVal(row, diffB);
            return Math.abs(d) > 1e-6;
        });
    }, [rows, onlyDiscrepancies, diffA, diffB]);

    const footerTotals = useMemo(() => {
        const t = { plan: 0, delivery: 0, payroll: 0, diffPos: 0, diffNeg: 0 };
        for (const r of displayedRows) {
            t.plan     += Number(r.planQty)     || 0;
            t.delivery += Number(r.deliveryQty) || 0;
            t.payroll  += Number(r.payrollQty)  || 0;
            const d = getVal(r, diffA) - getVal(r, diffB);
            if (d > 0) t.diffPos += d;
            else if (d < 0) t.diffNeg += d;
        }
        return t;
    }, [displayedRows, diffA, diffB]);

    const planLabel = (p) => {
        const from = p.periodStart ? formatUkDMYFromYmd(p.periodStart.slice(0, 10)) : "";
        const to   = p.periodEnd   ? formatUkDMYFromYmd(p.periodEnd.slice(0, 10))   : "";
        return `${p.title || "План"} (${from}–${to})`;
    };

    const colSpan = 1 + (showPlan ? 1 : 0) + 1 + (showPayroll ? 1 : 0) + 1;

    // In plan_check mode with plan selected → show hierarchical list
    const showDetailList = mode === "plan_check" && !!planId;

    return (
        <div className="production-tab-panel production-reconciliation-page mt-0 mt-md-2">
            {error ? <Alert variant="danger" className="py-2">{error}</Alert> : null}

            <div className="row g-2 align-items-end mb-3">
                {/* Mode */}
                <div className="col-12 col-sm-auto">
                    <label className="form-label small text-muted mb-1">Режим</label>
                    <AppSelect size="sm" value={mode} onChange={(e) => onModeChange(e.target.value)}>
                        <option value="delivery_check">Звірка поставки</option>
                        <option value="plan_check">Виконання плану</option>
                    </AppSelect>
                </div>

                {/* Week */}
                <div className="col-12 col-sm-auto">
                    <label className="form-label small text-muted mb-1">
                        Тиждень{weekRangeLabel ? ` (${weekRangeLabel})` : ""}
                    </label>
                    <AppDateField value={weekMonday} onChange={onWeekPick} size="sm" />
                </div>

                {/* Workshop */}
                <div className="col-12 col-sm-auto">
                    <label className="form-label small text-muted mb-1">Цех</label>
                    <AppSelect size="sm" value={workshopId} onChange={(e) => setWorkshopId(e.target.value)}>
                        <option value="all">Всі цехи</option>
                        {workshops.map((w) => (
                            <option key={w._id} value={w._id}>{w.name || w._id}</option>
                        ))}
                    </AppSelect>
                </div>

                {/* Plan — only in plan_check mode */}
                {showPlan && (
                    <div className="col-12 col-sm">
                        <label className="form-label small text-muted mb-1">План</label>
                        <AppSelect size="sm" value={planId} onChange={(e) => setPlanId(e.target.value)}>
                            <option value="">— без плану —</option>
                            {plans.map((p) => (
                                <option key={p._id} value={p._id}>{planLabel(p)}</option>
                            ))}
                        </AppSelect>
                    </div>
                )}

                {/* Only discrepancies */}
                <div className="col-auto d-flex align-items-center" style={{ paddingTop: "1.5rem" }}>
                    <AppCheckbox
                        id="reconciliation-only-diff"
                        label="Лише розбіжності"
                        checked={onlyDiscrepancies}
                        onChange={(e) => setOnlyDiscrepancies(e.target.checked)}
                    />
                </div>

                <div className="col-auto d-flex align-items-center" style={{ paddingTop: "1.5rem" }}>
                    {(loading || detailLoading) ? <Spinner animation="border" size="sm" role="status" aria-hidden /> : null}
                </div>
            </div>

            {/* Hierarchical plan detail list */}
            {showDetailList ? (
                <div className="materials-hub-table-scroll">
                    {detailLoading
                        ? null
                        : <PlanDetailList products={detail?.products} onlyDiscrepancies={onlyDiscrepancies} />
                    }
                </div>
            ) : (
                <div className="materials-hub-table-scroll">
                    <Table hover size="sm" className="align-middle production-reconciliation-table" style={{ tableLayout: "auto", width: "100%" }}>
                        <thead>
                            <tr>
                                <th className="text-start align-middle">Найменування</th>
                                {showPlan && (
                                    <th className="text-end text-nowrap align-middle ps-1 pe-1" style={{ fontSize: "0.8rem" }}>
                                        План, шт
                                    </th>
                                )}
                                <th className="text-end text-nowrap align-middle ps-1 pe-1" style={{ fontSize: "0.8rem" }}>
                                    Поставки, шт
                                </th>
                                {showPayroll && (
                                    <th className="text-end text-nowrap align-middle ps-1 pe-1" style={{ fontSize: "0.8rem" }}>
                                        Пошито швачками, шт
                                    </th>
                                )}
                                <th className="text-end text-nowrap align-middle ps-1 pe-1" style={{ fontSize: "0.8rem" }}>
                                    Різниця
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 && !loading ? (
                                <tr><td colSpan={colSpan} className="text-muted text-center py-4">Немає даних за обраний тиждень або цех.</td></tr>
                            ) : displayedRows.length === 0 && !loading ? (
                                <tr><td colSpan={colSpan} className="text-muted text-center py-4">Немає рядків із розбіжностями.</td></tr>
                            ) : (
                                displayedRows.map((row) => {
                                    const d = Math.round((getVal(row, diffA) - getVal(row, diffB)) * 100) / 100;
                                    const diffInvalid = showPlan && !planId;
                                    const diffStr = diffInvalid ? "—" : formatQty(d);
                                    const diffCls = diffInvalid ? "" : (d > 0 ? "text-success" : d < 0 ? "text-danger" : "");
                                    return (
                                        <tr key={row.key}>
                                            <td className="text-start text-wrap align-middle pe-3">
                                                <span className="fw-medium">{row.label}</span>
                                                {row.deliveryQty > 0 && row.deliveryBreakdown ? (
                                                    <span className="text-muted small ms-1">{formatQty(row.deliveryQty)} шт ({row.deliveryBreakdown})</span>
                                                ) : null}
                                            </td>
                                            {showPlan && (
                                                <td className="text-end text-nowrap font-monospace align-middle ps-1 pe-1">
                                                    {planId
                                                        ? <BreakdownCell value={formatQty(row.planQty)} breakdown={row.planBreakdown} id={`plan-tip-${row.key}`} />
                                                        : "—"}
                                                </td>
                                            )}
                                            <td className="text-end text-nowrap font-monospace align-middle ps-1 pe-1">
                                                <BreakdownCell value={formatQty(row.deliveryQty)} breakdown={row.deliveryBreakdown} id={`del-tip-${row.key}`} />
                                            </td>
                                            {showPayroll && (
                                                <td className="text-end text-nowrap font-monospace align-middle ps-1 pe-1">
                                                    {formatQty(row.payrollQty)}
                                                </td>
                                            )}
                                            <td className={`text-end text-nowrap font-monospace align-middle ps-1 pe-1 ${diffCls}`}>
                                                {diffStr}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                        {!loading && displayedRows.length > 0 && (
                            <tfoot className="table-light">
                                <tr>
                                    <td className="text-start fw-semibold align-middle pe-3 border-top">
                                        Усього{onlyDiscrepancies ? <span className="text-muted small fw-normal ms-1">(лише з розбіжностями)</span> : null}
                                    </td>
                                    {showPlan && (
                                        <td className="text-end text-nowrap font-monospace fw-semibold align-middle ps-1 pe-1 border-top">
                                            {planId ? formatQty(footerTotals.plan) : "—"}
                                        </td>
                                    )}
                                    <td className="text-end text-nowrap font-monospace fw-semibold align-middle ps-1 pe-1 border-top">
                                        {formatQty(footerTotals.delivery)}
                                    </td>
                                    {showPayroll && (
                                        <td className="text-end text-nowrap font-monospace fw-semibold align-middle ps-1 pe-1 border-top">
                                            {formatQty(footerTotals.payroll)}
                                        </td>
                                    )}
                                    <td className="text-end text-nowrap font-monospace align-middle ps-1 pe-1 border-top">
                                        {showPlan && !planId ? (
                                            <span className="fw-semibold">—</span>
                                        ) : (footerTotals.diffPos === 0 && footerTotals.diffNeg === 0) ? (
                                            <span className="fw-semibold text-muted">0</span>
                                        ) : (
                                            <span className="d-inline-flex align-items-baseline gap-2 flex-wrap justify-content-end">
                                                {footerTotals.diffPos !== 0 && (
                                                    <span className="fw-semibold text-success">+{formatQty(footerTotals.diffPos)}</span>
                                                )}
                                                {footerTotals.diffNeg !== 0 && (
                                                    <span className="fw-semibold text-danger">{formatQty(footerTotals.diffNeg)}</span>
                                                )}
                                                <span className="text-muted fw-normal" style={{ fontSize: "0.8rem" }}>
                                                    ({formatQty(Math.round((footerTotals.diffPos + footerTotals.diffNeg) * 100) / 100)})
                                                </span>
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            </tfoot>
                        )}
                    </Table>
                </div>
            )}
        </div>
    );
}
